import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TaxReturnStatusBadge } from '../components/tax/TaxReturnStatusBadge';
import { TaxReturnStepper } from '../components/tax/TaxReturnStepper';
import { FileText, AlertCircle, CheckCircle, DollarSign, MessageSquare, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatAUDate } from '../lib/dateUtils';

import { LegacyBanner } from '../components/LegacyBanner';

export default function TaxReturnReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workpaper, setWorkpaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);

  useEffect(() => {
    loadWorkpaper();
  }, [id]);

  const loadWorkpaper = async () => {
    try {
      const response = await api.get(`/api/tax-returns/${id}/workpaper`);
      setWorkpaper(response.data);
    } catch (error) {
      toast.error('Failed to load workpaper');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setSubmittingNote(true);
    try {
      await api.post(`/api/tax-returns/${id}/review-notes`, { note: newNote });
      toast.success('Note added');
      setNewNote('');
      loadWorkpaper();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleApprove = async () => {
    try {
      await api.patch(`/api/tax-returns/${id}`, { 
        status: 'ready_for_xero',
        review_completed: true,
        reviewed_by: 'current_user',
        reviewed_at: new Date().toISOString()
      });
      toast.success('Tax return approved and ready for Xero entry');
      navigate('/tax-returns');
    } catch (error) {
      toast.error('Failed to approve return');
    }
  };

  if (loading || !workpaper) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const { tax_return, questionnaire, documents, reconciliation_issues, review_notes, summary } = workpaper;

  return (
    <div className="p-6 space-y-6">
      <LegacyBanner module="Income Tax" />
      <fieldset disabled className="contents">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="review-title">
            Tax Return Review
          </h1>
          <p className="text-muted-foreground mt-1">
            {tax_return.client_name} - Tax Year {tax_return.tax_year}
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/tax-returns')} variant="outline">
            Back to Returns
          </Button>
          <Button 
            onClick={handleApprove}
            className="bg-green-600 hover:bg-green-700"
            data-testid="approve-return-button"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Approve for Xero
          </Button>
        </div>
      </div>

      <TaxReturnStepper status={tax_return.status} />

      {/* Status & Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Status</p>
              <TaxReturnStatusBadge status={tax_return.status} />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Income</p>
                <p className="text-2xl font-bold text-green-700" style={{fontFamily: 'IBM Plex Mono, monospace'}}>
                  ${summary.total_income.toLocaleString('en-AU')}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Deductions</p>
                <p className="text-2xl font-bold text-red-700" style={{fontFamily: 'IBM Plex Mono, monospace'}}>
                  ${summary.total_deductions.toLocaleString('en-AU')}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taxable Income</p>
                <p className="text-2xl font-bold text-foreground" style={{fontFamily: 'IBM Plex Mono, monospace'}}>
                  ${summary.taxable_income.toLocaleString('en-AU')}
                </p>
              </div>
              <FileText className="w-8 h-8 text-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reconciliation Issues Alert */}
      {reconciliation_issues.length > 0 && (
        <Card className="border-l-4 border-l-red-500 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-2">
                  {reconciliation_issues.length} Reconciliation Issue(s) Detected
                </h3>
                <div className="space-y-2">
                  {reconciliation_issues.slice(0, 5).map((issue) => (
                    <div key={issue.id} className="flex items-start gap-2 text-sm">
                      <Badge className={
                        issue.severity === 'high' ? 'bg-red-600' :
                        issue.severity === 'medium' ? 'bg-amber-600' :
                        'bg-blue-600'
                      }>
                        {issue.severity}
                      </Badge>
                      <span className="text-red-800">{issue.description}</span>
                    </div>
                  ))}
                  {reconciliation_issues.length > 5 && (
                    <p className="text-sm text-red-700">...and {reconciliation_issues.length - 5} more</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="income">Income</TabsTrigger>
          <TabsTrigger value="deductions">Deductions</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes ({review_notes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Name:</span> <span className="font-semibold">{questionnaire.personal_details?.full_name || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">DOB:</span> <span className="font-semibold">{questionnaire.personal_details?.dob ? formatAUDate(questionnaire.personal_details.dob) : 'N/A'}</span></div>
                <div><span className="text-muted-foreground">TFN:</span> <span className="font-mono font-semibold">{questionnaire.personal_details?.tfn ? '*** *** ' + questionnaire.personal_details.tfn.slice(-3) : 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Residency:</span> <span className="font-semibold">{questionnaire.personal_details?.residency || 'N/A'}</span></div>
                <div className="col-span-2"><span className="text-muted-foreground">Address:</span> <span className="font-semibold">{questionnaire.personal_details?.address || 'N/A'}</span></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="font-semibold">Total Income</span>
                  <span className="font-mono font-bold text-green-700">${summary.total_income.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="font-semibold">Total Deductions</span>
                  <span className="font-mono font-bold text-red-700">${summary.total_deductions.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t-2">
                  <span className="font-semibold text-lg">Taxable Income</span>
                  <span className="font-mono font-bold text-2xl text-foreground">${summary.taxable_income.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <FileText className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <p className="text-sm text-muted-foreground">Documents</p>
                <p className="text-2xl font-bold">{summary.documents_uploaded}</p>
                <p className="text-xs text-muted-foreground mt-1">{summary.documents_extracted} extracted</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6 text-center">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-600" />
                <p className="text-sm text-muted-foreground">Issues</p>
                <p className="text-2xl font-bold">{summary.reconciliation_issues}</p>
                <p className="text-xs text-red-600 mt-1">{summary.high_severity_issues} high severity</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6 text-center">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <p className="text-sm text-muted-foreground">Review Notes</p>
                <p className="text-2xl font-bold">{summary.review_notes_count}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="income">
          <Card>
            <CardHeader>
              <CardTitle>Income Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(questionnaire.income || {}).map(([key, value]) => (
                  value && parseFloat(value) > 0 && (
                    <div key={key} className="flex justify-between items-center py-2 border-b">
                      <span className="text-foreground/90 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-mono font-semibold">${parseFloat(value).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deductions">
          <Card>
            <CardHeader>
              <CardTitle>Deductions Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(questionnaire.deductions || {}).map(([key, value]) => (
                  value && parseFloat(value) > 0 && (
                    <div key={key} className="flex justify-between items-center py-2 border-b">
                      <span className="text-foreground/90 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-mono font-semibold">${parseFloat(value).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Uploaded Documents</CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No documents uploaded</p>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground/70" />
                        <div>
                          <p className="font-medium text-foreground">{doc.filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.document_category.replace(/_/g, ' ')} - Uploaded {formatAUDate(doc.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      <Badge className={
                        doc.extraction_status === 'completed' ? 'bg-green-100 text-green-800' :
                        doc.extraction_status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        doc.extraction_status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-muted text-foreground'
                      }>
                        {doc.extraction_status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <Card>
            <CardHeader>
              <CardTitle>Review Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a review note or comment..."
                  rows={3}
                  data-testid="review-note-input"
                />
                <Button
                  onClick={handleAddNote}
                  disabled={submittingNote}
                  size="sm"
                  className="bg-primary hover:bg-primary/90"
                  data-testid="add-note-button"
                >
                  {submittingNote ? 'Adding...' : 'Add Note'}
                </Button>
              </div>

              {review_notes.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No review notes yet</p>
              ) : (
                <div className="space-y-3 mt-6">
                  {review_notes.map((note) => (
                    <div key={note.id} className="p-4 bg-muted/50 rounded-lg border">
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-semibold text-foreground">{note.reviewer_name}</span>
                        <span className="text-xs text-muted-foreground">{formatAUDate(note.created_at)}</span>
                      </div>
                      <p className="text-sm text-foreground/90">{note.note}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </fieldset>
    </div>
  );
}
